import {
  ColDef,
  RowClassParams,
  RowStyle,
  themeAlpine,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo } from "react";

import { IgTableWrapper } from "@intelligems/ig-theme";
import {
  AgencySubscriptionDto,
  AgencySubscriptionItemDto,
  AgencySubscriptionProductLinkDto,
  FullAgencyDto,
} from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { DecimalFormat } from "@src/features/admin/components/utils";
import { NameColumn } from "@src/features/admin/pages/agencies/components/Tables/AssignedOrgsTable/renderers/NameColumn";
import { RevokeAccessColumn } from "@src/features/admin/pages/agencies/components/Tables/AssignedOrgsTable/renderers/RevokeAccessColumn";
import { ViewUsageColumn } from "@src/features/admin/pages/agencies/components/Tables/AssignedOrgsTable/renderers/ViewUsageColumn";
import { theme } from "@src/theme";

interface AssignedOrgsTable {
  subscription: AgencySubscriptionDto;
  item: AgencySubscriptionItemDto;
  agency: FullAgencyDto;
}

const myTheme = themeAlpine.withParams({
  fontFamily: theme.typography.fontFamily,
});

export const AssignedOrgsTable = ({
  subscription,
  item,
  agency,
}: AssignedOrgsTable) => {
  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
    };
  }, []);

  // Calculate Grand Total
  const totalRow: AgencySubscriptionProductLinkDto = useMemo(() => {
    const totalValue = item.stripeProductLinks.reduce(
      (sum, row) => sum + row.meterValue,
      0
    );
    return {
      id: "grandTotal",
      name: "",
      meterValue: totalValue,
      stripeProductId: "",
      stripeSubscriptionLinkId: "",
      organization: {
        id: "",
        name: "",
        shopId: "",
      },
    };
  }, [item.stripeProductLinks]);

  const getRowStyle = (
    params: RowClassParams<AgencySubscriptionProductLinkDto>
  ) => {
    if (params.data?.id === "grandTotal") {
      return {
        fontWeight: "bold",
        backgroundColor: "#f9f9f9",
        height: "24px",
      } as RowStyle;
    }
    return undefined;
  };

  const columnDefs: ColDef<AgencySubscriptionProductLinkDto & { "": "" }>[] =
    useMemo(
      () => [
        {
          headerName: "",
          field: "",
          cellStyle: { justifyContent: "start" },
          cellRenderer: NameColumn,
        },

        {
          headerName: "Relative Charge",
          field: "meterValue",
          valueGetter: (
            params: ValueGetterParams<AgencySubscriptionProductLinkDto>
          ) => (params.data?.id === "grandTotal" ? null : item.meter.meterUnit),
        },
        {
          headerName: "Orders",
          field: "meterValue",
          valueFormatter: (
            params: ValueFormatterParams<AgencySubscriptionProductLinkDto>
          ) =>
            params.data?.meterValue
              ? DecimalFormat.format(params.data.meterValue)
              : "-",
        },
        {
          headerName: "",
          field: "",
          cellRenderer: ViewUsageColumn,
        },
        {
          headerName: "",
          field: "",
          cellRenderer: RevokeAccessColumn,
          cellStyle: { justifyContent: "end" },
        },
      ],
      []
    );

  const data = useMemo(
    () =>
      item.stripeProductLinks.map((spl) => ({
        ...spl,
        dangling: !agency.organizationAgencyLinks.find(
          (oal) => oal.organization.id === spl.organization.id
        ),
      })),
    [item.stripeProductLinks, agency.organizationAgencyLinks]
  );

  return (
    <Grid
      item
      sx={{
        marginTop: "10px",
        width: "100%",
      }}
    >
      <IgTableWrapper minHeight={"100px"} height={null}>
        <AgGridReact<AgencySubscriptionProductLinkDto>
          theme={myTheme}
          rowData={[...data, totalRow]}
          domLayout={"autoHeight"}
          context={{ item, agency, subscription }}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          headerHeight={25}
          suppressCellFocus={true}
          suppressContextMenu={true}
          suppressMovableColumns={true}
          suppressRowHoverHighlight={true}
          getRowStyle={getRowStyle}
        />
      </IgTableWrapper>
    </Grid>
  );
};
